import React, { useState, useEffect, useRef } from 'react';
import ConfirmModal from '@/components/ConfirmModal';
import Toast from '@/components/Toast';
import LoadingOverlay from '@/components/LoadingOverlay';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { Article } from '@/types/article';
import { Category } from '@/types/category';
import { adminApi } from '@/features/admin/components/adminApi';
import { useAuth } from '@/contexts/AuthContext';
import {
  useAdminCategoriesByCountryQuery,
  useAdminTopicsAsCategoriesQuery,
} from '@hooks/useConfigureQueries';

export const ArticleSummaryReview: React.FC = () => {
  const { user } = useAuth();
  const { country, id: articleId } = useParams<{ country?: string; id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Loading article details...');
  const [article, setArticle] = useState<Article | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [showPublishConfirm, setShowApproveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tags, setTags] = useState<Array<{ tag: string }>>([]);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [addTagForm, setAddTagForm] = useState({
    tag: '',
  });
  const { data: adminCountryCategories = [] } = useAdminCategoriesByCountryQuery(
    country,
    Boolean(country)
  );
  const { data: adminTopicCategories = [] } = useAdminTopicsAsCategoriesQuery(!country);
  const categories: Category[] = country ? adminCountryCategories : adminTopicCategories;
  const notifTimeout = useRef<number | null>(null);

  const getLevelLabel = (level: 'A' | 'B' | 'C'): string => {
    if (level === 'A') return 'Easy';
    if (level === 'B') return 'Medium';
    return 'Hard';
  };

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    };
  }, []);

  const fetchArticleDetail = async (country: string, articleId: string) => {
    setLoadingText('Loading article details...');
    setIsLoading(true);
    try {
      const data = await articleAdminApi.getById(country, articleId);
      setArticle(data);
    } catch (error) {
      showNotification('Failed to fetch article details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (articleId) {
      fetchArticleDetail(country || '', articleId);
    }
  }, [country, articleId]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const data = await adminApi.getAllTags();
        setTags(data || []);
      } catch (error) {
        showNotification('Failed to load tags', 'error');
      }
    };
    fetchTags();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setArticle(prev => (prev ? { ...prev, [e.target.name]: e.target.value } : prev));
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions).map(option => option.value);
    setArticle(prev => (prev ? { ...prev, tags: selected } : prev));
  };

  const handleAddTag = async () => {
    if (!addTagForm.tag.trim()) {
      showNotification('Please enter a tag', 'error');
      return;
    }
    setLoadingText('Adding tag...');
    setIsLoading(true);
    try {
      await adminApi.addTag(addTagForm);
      showNotification('Tag added successfully', 'success');
      setShowAddTagModal(false);
      setAddTagForm({ tag: '' });
      const data = await adminApi.getAllTags();
      setTags(data || []);
    } catch (error) {
      showNotification('Failed to add tag', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setLoadingText('Saving article...');
    setIsLoading(true);
    try {
      await articleAdminApi.update(country!, articleId!, article!);
      // Navigate to articles list and pass notification via location.state
      navigate('/editor/articles', {
        state: {
          notification: { message: 'Article saved successfully!', type: 'success' },
          activeTab: 'summary',
        },
      });
    } catch (error) {
      showNotification('Failed to save article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    setShowApproveConfirm(true);
  };

  const performApprove = async () => {
    if (!article) return;
    // Close the confirm modal right away so the UI isn't blocked by it
    setShowApproveConfirm(false);
    setLoadingText('Approving article and generating IPA, translations, and TTS...');
    setIsLoading(true);
    try {
      article.summaryReviewedBy = user?.name || 'Unknown';
      // call backend approve endpoint which will update article, extract vocab, generate tts and translations
      // This process may take a while, so don't await the response and navigate back to the list with a notification,
      // the list page will handle refetching the updated article details
      articleAdminApi.approveSummary(country!, articleId!, article);
      setTimeout(() => {
        setIsLoading(false);
        navigate('/editor/articles', {
          state: {
            notification: { message: 'Article approved', type: 'success' },
            activeTab: 'summary',
          },
        });
      }, 2000);
    } catch (err) {
      showNotification('Failed to approve article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const performDelete = async () => {
    if (!articleId) return;

    setShowDeleteConfirm(false);
    setLoadingText('Deleting article resources...');
    setIsLoading(true);

    try {
      await articleAdminApi.delete(country || '', articleId);
      navigate('/editor/articles', {
        state: {
          notification: { message: 'Article deleted successfully!', type: 'success' },
          activeTab: 'summary',
        },
      });
    } catch (error) {
      showNotification('Failed to delete article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {isLoading && <LoadingOverlay text={loadingText} />}
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        open={showPublishConfirm}
        title="Confirm Approve Summary"
        description="Are you sure you want to approve this article summaries?"
        confirmText="Yes, approve"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performApprove}
        onCancel={() => setShowApproveConfirm(false)}
      />
      <ConfirmModal
        open={showDeleteConfirm}
        title="Delete Article"
        description="Are you sure you want to delete this article? This will permanently remove its TTS files, article image, tag mappings, and database record."
        confirmText="Delete article"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      {showAddTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Tag</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="tag" className="block text-sm font-medium text-gray-700 mb-2">
                  Tag <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="tag"
                  value={addTagForm.tag}
                  onChange={e => setAddTagForm(prev => ({ ...prev, tag: e.target.value }))}
                  placeholder="e.g., education, politics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddTagModal(false);
                  setAddTagForm({ tag: '' });
                }}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleAddTag}
                loading={isLoading}
                className="flex-1"
              >
                Add Tag
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Review Article Summary</h1>
          <p className="text-gray-600 mt-2">Make changes to the article content</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === 'admin' && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDelete}
              disabled={isLoading || !articleId}
              className="border-red-300 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-700"
            >
              Delete Article
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/editor/articles', { state: { activeTab: 'summary' } })}
          >
            ← Back to Articles
          </Button>
        </div>
      </div>

      <Card className="p-6">
        <form className="space-y-6">
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            {article?.isSummaryReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                Reviewed
              </span>
            )}
            {!article?.isSummaryReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                Pending Review
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={article?.category ?? ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <option value="">Select category</option>
                {categories.map(category => (
                  <option key={category.categoryCode} value={category.categoryCode}>
                    {category.categoryNameEN}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="publishedAt" className="block text-sm font-medium text-gray-700 mb-1">
              Published at
            </label>
            <span className="text-gray-700">
              {article ? new Date(article?.publishedAt).toLocaleString() : ''}
            </span>
          </div>

          <div>
            <label htmlFor="createdAt" className="block text-sm font-medium text-gray-700 mb-1">
              Created at
            </label>
            <span className="text-gray-700">
              {article ? new Date(article?.createdAt).toLocaleString() : ''}
            </span>
          </div>

          {(article?.articleTopic?.trim() || article?.description?.trim()) && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">Generation Input</h3>

              {article?.articleTopic?.trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Topic used for generation
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {article.articleTopic}
                  </p>
                </div>
              )}

              {article?.description?.trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description used for generation
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{article.description}</p>
                </div>
              )}
            </div>
          )}

          {country && country.toUpperCase() !== 'VI' && (
            <div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="tag" className="block text-sm font-medium text-gray-700">
                    Tags <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  {user?.role === 'admin' && (
                    <button
                      type="button"
                      onClick={() => setShowAddTagModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      + Add Tag
                    </button>
                  )}
                </div>
                <select
                  id="tag"
                  name="tag"
                  multiple
                  value={article?.tags ?? []}
                  onChange={handleTagChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {tags.map(t => (
                    <option key={t.tag} value={t.tag}>
                      {t.tag}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={article?.title ?? ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                  Slug
                </label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  value={article?.slug ?? ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="textALevelEN"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content level A ({getLevelLabel('A')})
                </label>
                <textarea
                  id="textALevelEN"
                  name="textALevelEN"
                  value={article?.textALevelEN ?? ''}
                  onChange={handleChange}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="textBLevelEN"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content level B ({getLevelLabel('B')})
                </label>
                <textarea
                  id="textBLevelEN"
                  name="textBLevelEN"
                  value={article?.textBLevelEN ?? ''}
                  onChange={handleChange}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="textCLevelEN"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content level C ({getLevelLabel('C')})
                </label>
                <textarea
                  id="textCLevelEN"
                  name="textCLevelEN"
                  value={article?.textCLevelEN ?? ''}
                  onChange={handleChange}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {(!country || country.toUpperCase() === 'VI') && (
            <div>
              {/* US */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="tag" className="block text-sm font-medium text-gray-700">
                    Tags <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  {user?.role === 'admin' && (
                    <button
                      type="button"
                      onClick={() => setShowAddTagModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      + Add Tag
                    </button>
                  )}
                </div>
                <select
                  id="tag"
                  name="tag"
                  multiple
                  value={article?.tags ?? []}
                  onChange={handleTagChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {tags.map(t => (
                    <option key={t.tag} value={t.tag}>
                      {t.tag}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="titleUS" className="block text-sm font-medium text-gray-700 mb-1">
                  Title US
                </label>
                <input
                  id="titleUS"
                  name="titleUS"
                  type="text"
                  value={article?.titleUS ?? ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="slugUS" className="block text-sm font-medium text-gray-700 mb-1">
                  Slug US
                </label>
                <input
                  id="slugUS"
                  name="slugUS"
                  type="text"
                  value={article?.slugUS ?? ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="textALevelUS"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content level A ({getLevelLabel('A')})
                </label>
                <textarea
                  id="textALevelUS"
                  name="textALevelUS"
                  value={article?.textALevelUS ?? ''}
                  onChange={handleChange}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="textBLevelUS"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content level B ({getLevelLabel('B')})
                </label>
                <textarea
                  id="textBLevelUS"
                  name="textBLevelUS"
                  value={article?.textBLevelUS ?? ''}
                  onChange={handleChange}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="textCLevelUS"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content level C ({getLevelLabel('C')})
                </label>
                <textarea
                  id="textCLevelUS"
                  name="textCLevelUS"
                  value={article?.textCLevelUS ?? ''}
                  onChange={handleChange}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4 border-t">
            {/* Display Save Draft button if user is admin or article is not reviewed */}
            {(user?.role === 'admin' || !article?.isSummaryReviewed) && (
              <Button type="button" onClick={handleSave} disabled={isLoading} className="flex-1">
                {isLoading ? 'Saving...' : 'Save Draft'}
              </Button>
            )}

            <Button
              type="button"
              onClick={handleApprove}
              disabled={isLoading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Approve Summary
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/editor/articles', { state: { activeTab: 'summary' } })}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
